import { useEffect, useState } from "react";
import { Clock, MapPin } from "lucide-react";
import { formatEUR, formatTimeLeft, timeUrgency } from "@/lib/format";
import { openInNewTab } from "@/lib/dom";
import LotCardMedia from "@/components/LotCardMedia";
import CopyLinkButton from "@/components/CopyLinkButton";
import ShippingInfo from "@/components/ShippingInfo";

const TIME_COLORS = {
  urgent: "text-red-400",
  soon: "text-gold",
  neutral: "text-white/55",
};

function useCountdown(initial) {
  const [remaining, setRemaining] = useState(initial);
  useEffect(() => {
    setRemaining(initial);
    if (initial === null || initial === undefined) return undefined;
    const id = setInterval(
      () => setRemaining((s) => (s !== null && s > 0 ? s - 1 : s)),
      1000,
    );
    return () => clearInterval(id);
  }, [initial]);
  return remaining;
}

function handleCardClick(e, url) {
  if (e.metaKey || e.ctrlKey || e.shiftKey) return;
  e.preventDefault();
  openInNewTab(url);
}

export default function LotCard({ lot, pickupMode = false }) {
  const remaining = useCountdown(lot.seconds_remaining);
  const timeColor = TIME_COLORS[timeUrgency(remaining)];
  const bidLabel = lot.current_bid_eur ? "Offerta attuale" : "Offerta di partenza";
  const bidValue = lot.current_bid_eur || lot.starting_bid_eur || 0;
  const estimatedTotal = bidValue ? (bidValue * 1.09) + 3 + (lot.shipping_eur || 0) : null;

  return (
    <a
      href={lot.url}
      target="_blank"
      rel="noopener"
      onClick={(e) => handleCardClick(e, lot.url)}
      className="group block focus:outline-none cursor-pointer"
      data-testid={`lot-card-${lot.id}`}
    >
      <LotCardMedia lot={lot} />

      {pickupMode && (lot.pickup_city || lot.pickup_country || lot.seller_country) && (
        <div
          className="flex items-center gap-1.5 mb-2 text-gold"
          data-testid={`lot-pickup-city-${lot.id}`}
        >
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-sm font-medium tracking-wide">
            {[lot.pickup_city, lot.pickup_country || lot.seller_country].filter(Boolean).join(", ")}
          </span>
        </div>
      )}

      <h3
        className="font-display text-base sm:text-lg leading-snug line-clamp-2 group-hover:text-gold transition-colors duration-300"
        data-testid={`lot-title-${lot.id}`}
      >
        {lot.title}
      </h3>
      {lot.subtitle && (
        <p className="text-xs text-white/40 mt-1.5 line-clamp-1">{lot.subtitle}</p>
      )}

      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{bidLabel}</div>
          <div
            className="font-body font-light text-xl text-gold mt-1"
            data-testid={`lot-price-${lot.id}`}
          >
            {formatEUR(bidValue)}
          </div>
          {estimatedTotal !== null && (
            <div className="mt-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Costo stimato</div>
              <div className="font-body font-semibold text-2xl text-gold">{formatEUR(estimatedTotal)}</div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <CopyLinkButton url={lot.url} testid={`lot-copy-${lot.id}`} />
          <div className={`text-sm flex items-center gap-1 ${timeColor}`} data-testid={`lot-time-${lot.id}`}>
            <Clock className="w-3.5 h-3.5" />
            {formatTimeLeft(remaining)}
          </div>
        </div>
      </div>

      <ShippingInfo lot={lot} />
    </a>
  );
}
